/**
 * @file 请求并发队列控制模块
 * @description 基于 p-queue 实现全局请求并发限制、FIFO 排队、优先级调度、同接口防抖
 */

import PQueue from 'p-queue';

const DEFAULT_CONCURRENCY = 4;

const requestQueue = new PQueue({
    concurrency: DEFAULT_CONCURRENCY,
    autoStart: true
});

// ========== 同接口防抖 ==========

const pendingRequests = new Map();

function getDedupKey(config) {
    if (config.dedupKey) {
        return config.dedupKey;
    }
    return `${config.method}:${config.url}`;
}

/**
 * 同接口防抖：新请求进来时取消相同 key 的旧请求
 * @param {Object} config - axios 请求配置
 * @returns {Function} cleanup - 在请求 .finally() 中调用以移除已完成的条目
 */
export function cancelDuplicate(config) {
    const key = getDedupKey(config);

    if (pendingRequests.has(key)) {
        const prev = pendingRequests.get(key);
        prev.abort();
    }

    const controller = new AbortController();
    config.signal = controller.signal;
    pendingRequests.set(key, controller);

    return function cleanup() {
        if (pendingRequests.get(key) === controller) {
            pendingRequests.delete(key);
        }
    };
}

// ========== 任务入队 ==========

/**
 * 将请求任务加入并发队列
 * @param {Function} fn - 返回 Promise 的函数
 * @param {Object} options - { priority: number }
 * @returns {Promise}
 */
export function enqueue(fn, options = {}) {
    const {priority = 0} = options;
    return requestQueue.add(fn, {priority});
}

// ========== 辅助方法（调试/运维） ==========

export function getQueueStats() {
    return {
        size: requestQueue.size,
        pending: requestQueue.pending,
        concurrency: requestQueue.concurrency,
        isPaused: requestQueue.isPaused
    };
}

export function setConcurrency(concurrency) {
    if (concurrency >= 1) {
        requestQueue.concurrency = concurrency;
    }
}

export function clearQueue() {
    requestQueue.clear();
}
